// True when the user's employment type is a contractor/consultant (no leave
// entitlement). Tolerant of employeeType being an object ({ name }) or a string,
// or a flat `employeeTypeName` field. Matches "Contractor", "Consultant/Contractor".
export const isContractor = user => {
  const et = user?.employeeType;
  const name = typeof et === 'string' ? et : et?.name;
  return String(name ?? user?.employeeTypeName ?? '')
    .toLowerCase()
    .includes('contractor');
};

// Role-based permissions configuration
// Based on the provided permission matrix
export const ROLES = {
  EMPLOYEE: 'EMPLOYEE',
  REPORTING_MANAGER: 'REPORTING_MANAGER',
  HR_ADMIN: 'HR_ADMIN',
  IT_ADMIN: 'IT_ADMIN',
  ADMIN: 'ADMIN',
  SUPER_ADMIN: 'SUPER_ADMIN',
  ACCOUNTS_ADMIN: 'ACCOUNTS_ADMIN',
  PROJECT_MANAGER: 'PROJECT_MANAGER',
};
// Permission levels
export const PERMISSIONS = {
  VIEW: 'VIEW',
  ADD: 'ADD',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  ASSIGN_ROLE: 'ASSIGN_ROLE',
  ADD_ADMIN: 'ADD_ADMIN',
};
// Module definitions
export const MODULES = {
  EMPLOYEE_MANAGEMENT: 'EMPLOYEE_MANAGEMENT',
  HOLIDAY: 'HOLIDAY',
  POLICIES: 'POLICIES',
  ANNOUNCEMENTS: 'ANNOUNCEMENTS',
  PERFORMANCE_MANAGEMENT: 'PERFORMANCE_MANAGEMENT',
  SURVEYS: 'SURVEYS',
  CERTIFICATIONS: 'CERTIFICATIONS',
};
// Permission matrix based on the provided table
const PERMISSION_MATRIX = {
  [MODULES.EMPLOYEE_MANAGEMENT]: {
    [PERMISSIONS.VIEW]: [
      ROLES.EMPLOYEE,
      ROLES.REPORTING_MANAGER,
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.ADD_ADMIN]: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
    [PERMISSIONS.ASSIGN_ROLE]: [ROLES.HR_ADMIN, ROLES.ADMIN, ROLES.SUPER_ADMIN],
    [PERMISSIONS.EDIT]: [
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.DELETE]: [
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
  },
  [MODULES.HOLIDAY]: {
    [PERMISSIONS.VIEW]: [
      ROLES.EMPLOYEE,
      ROLES.REPORTING_MANAGER,
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.ADD]: [ROLES.HR_ADMIN, ROLES.SUPER_ADMIN],
    [PERMISSIONS.EDIT]: [ROLES.HR_ADMIN, ROLES.SUPER_ADMIN],
    [PERMISSIONS.DELETE]: [ROLES.HR_ADMIN, ROLES.SUPER_ADMIN],
  },
  [MODULES.POLICIES]: {
    [PERMISSIONS.VIEW]: [
      ROLES.EMPLOYEE,
      ROLES.REPORTING_MANAGER,
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
  },
  [MODULES.ANNOUNCEMENTS]: {
    [PERMISSIONS.VIEW]: [
      ROLES.EMPLOYEE,
      ROLES.REPORTING_MANAGER,
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.ADD]: [ROLES.HR_ADMIN, ROLES.ADMIN, ROLES.SUPER_ADMIN],
    [PERMISSIONS.EDIT]: [ROLES.HR_ADMIN, ROLES.ADMIN, ROLES.SUPER_ADMIN],
    [PERMISSIONS.DELETE]: [ROLES.HR_ADMIN, ROLES.ADMIN, ROLES.SUPER_ADMIN],
  },
  [MODULES.PERFORMANCE_MANAGEMENT]: {
    [PERMISSIONS.VIEW]: [
      ROLES.EMPLOYEE,
      ROLES.REPORTING_MANAGER,
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.ADD]: [ROLES.HR_ADMIN, ROLES.ADMIN, ROLES.SUPER_ADMIN],
    [PERMISSIONS.ASSIGN_ROLE]: [ROLES.REPORTING_MANAGER, ROLES.ADMIN],
  },
  [MODULES.SURVEYS]: {
    [PERMISSIONS.VIEW]: [
      ROLES.EMPLOYEE,
      ROLES.REPORTING_MANAGER,
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.ADD]: [
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.EDIT]: [
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.DELETE]: [
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
  },
  [MODULES.CERTIFICATIONS]: {
    [PERMISSIONS.VIEW]: [
      ROLES.EMPLOYEE,
      ROLES.REPORTING_MANAGER,
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.ADD]: [
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.EDIT]: [
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
    [PERMISSIONS.DELETE]: [
      ROLES.HR_ADMIN,
      ROLES.IT_ADMIN,
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
    ],
  },
};
/**
 * Check if a user has a specific permission for a module
 * @param {Array} userRoles - Array of user roles
 * @param {string} module - Module name
 * @param {string} permission - Permission type
 * @returns {boolean} - Whether user has permission
 */
export const hasPermission = (userRoles, module, permission) => {
  if (!userRoles || !Array.isArray(userRoles) || userRoles.length === 0) {
    return false;
  }
  const modulePermissions = PERMISSION_MATRIX[module];
  if (!modulePermissions) {
    return false;
  }
  const allowedRoles = modulePermissions[permission];
  if (!allowedRoles) {
    return false;
  }
  return userRoles.some(role => allowedRoles.includes(role));
};
/**
 * Check if user has any of the specified permissions for a module
 * @param {Array} userRoles - Array of user roles
 * @param {string} module - Module name
 * @param {Array} permissions - Array of permission types
 * @returns {boolean} - Whether user has any of the permissions
 */
export const hasAnyPermission = (userRoles, module, permissions) => {
  return permissions.some(permission =>
    hasPermission(userRoles, module, permission)
  );
};
/**
 * Check if user has all of the specified permissions for a module
 * @param {Array} userRoles - Array of user roles
 * @param {string} module - Module name
 * @param {Array} permissions - Array of permission types
 * @returns {boolean} - Whether user has all permissions
 */
export const hasAllPermissions = (userRoles, module, permissions) => {
  return permissions.every(permission =>
    hasPermission(userRoles, module, permission)
  );
};
/**
 * Get all permissions for a user in a specific module
 * @param {Array} userRoles - Array of user roles
 * @param {string} module - Module name
 * @returns {Array} - Array of permissions the user has
 */
export const getUserPermissions = (userRoles, module) => {
  if (!userRoles || !Array.isArray(userRoles) || userRoles.length === 0) {
    return [];
  }
  const modulePermissions = PERMISSION_MATRIX[module];
  if (!modulePermissions) {
    return [];
  }
  return Object.keys(modulePermissions).filter(permission =>
    hasPermission(userRoles, module, permission)
  );
};
/**
 * Check if user is an admin (HR_ADMIN, IT_ADMIN, ADMIN, or SUPER_ADMIN)
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} - Whether user is an admin
 */
export const isAdmin = userRoles => {
  if (!userRoles || !Array.isArray(userRoles)) {
    return false;
  }
  const adminRoles = [ROLES.HR_ADMIN, ROLES.ADMIN, ROLES.SUPER_ADMIN];
  return userRoles.some(role => adminRoles.includes(role));
};
/**
 * Check if user is a super admin
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} - Whether user is a super admin
 */
export const isSuperAdmin = userRoles => {
  if (!userRoles || !Array.isArray(userRoles)) {
    return false;
  }
  return userRoles.includes(ROLES.SUPER_ADMIN);
};
/**
 * Check if user can manage content (add/edit/delete)
 * @param {Array} userRoles - Array of user roles
 * @param {string} module - Module name
 * @returns {boolean} - Whether user can manage content
 */
export const canManageContent = (userRoles, module) => {
  return hasAnyPermission(userRoles, module, [
    PERMISSIONS.ADD,
    PERMISSIONS.EDIT,
    PERMISSIONS.DELETE,
  ]);
};
/**
 * Check if user can view content
 * @param {Array} userRoles - Array of user roles
 * @param {string} module - Module name
 * @returns {boolean} - Whether user can view content
 */
export const canViewContent = (userRoles, module) => {
  return hasPermission(userRoles, module, PERMISSIONS.VIEW);
};
// Convenience functions for specific modules
export const canManageAnnouncements = userRoles =>
  canManageContent(userRoles, MODULES.ANNOUNCEMENTS);
export const canViewAnnouncements = userRoles =>
  canViewContent(userRoles, MODULES.ANNOUNCEMENTS);
export const canManageHolidays = userRoles =>
  canManageContent(userRoles, MODULES.HOLIDAY);
export const canViewHolidays = userRoles =>
  canViewContent(userRoles, MODULES.HOLIDAY);
export const canViewPolicies = userRoles =>
  canViewContent(userRoles, MODULES.POLICIES);
export const canManageEmployees = userRoles =>
  canManageContent(userRoles, MODULES.EMPLOYEE_MANAGEMENT);
export const canViewEmployees = userRoles =>
  canViewContent(userRoles, MODULES.EMPLOYEE_MANAGEMENT);
/**
 * True when the user has IT_ADMIN but no role that grants full employee-directory edits.
 * Those users are view-only on the directory; combined roles (e.g. SUPER_ADMIN + IT_ADMIN) are not view-only.
 */
export const isEmployeeDirectoryItAdminViewOnly = userRoles => {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  if (!userRoles.includes(ROLES.IT_ADMIN)) return false;
  return !userRoles.some(role =>
    [ROLES.HR_ADMIN, ROLES.ADMIN, ROLES.SUPER_ADMIN].includes(role)
  );
};
export const canEditEmployees = userRoles => {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  if (isEmployeeDirectoryItAdminViewOnly(userRoles)) return false;
  return canManageContent(userRoles, MODULES.EMPLOYEE_MANAGEMENT);
};
export const canAssignRoles = userRoles =>
  hasPermission(
    userRoles,
    MODULES.EMPLOYEE_MANAGEMENT,
    PERMISSIONS.ASSIGN_ROLE
  );
export const canAddAdmin = userRoles =>
  hasPermission(userRoles, MODULES.EMPLOYEE_MANAGEMENT, PERMISSIONS.ADD_ADMIN);
/**
 * Check if user should see Employee Directory tab
 * Users with only EMPLOYEE, REPORTING_MANAGER, or IT_ADMIN roles should not see it
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} - Whether user should see Employee Directory
 */
export const canSeeEmployeeDirectory = userRoles => {
  // Allow all authenticated users to see the Employee Directory
  return Array.isArray(userRoles) && userRoles.length > 0;
};
/**
 * Check if user should see the Project Management tab / Projects menu.
 * Open to every authenticated user — viewing projects (list + detail tabs)
 * carries no role restriction. Create/Edit/Delete stay gated separately via
 * canManageProjects (Project Manager / Super Admin only).
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean}
 */
export const canSeeProjectManagement = () => true;

// All Invoices tab (cross-project invoices) — Accounts Admin and Super Admin.
export const canAccessAllInvoices = userRoles => {
  if (!Array.isArray(userRoles)) return false;
  return userRoles.some(role =>
    [ROLES.ACCOUNTS_ADMIN, ROLES.SUPER_ADMIN].includes(role)
  );
};

// Create / Edit / Delete projects — restricted to Project Manager and
// Super Admin (matches the Project API's Create access).
export const canManageProjects = userRoles => {
  if (!Array.isArray(userRoles)) return false;
  return userRoles.some(role =>
    [ROLES.PROJECT_MANAGER, ROLES.SUPER_ADMIN].includes(role)
  );
};

// View team-member currency / rate — managers plus Accounts Admin (read-only).
export const canViewProjectRates = userRoles => {
  if (!Array.isArray(userRoles)) return false;
  return userRoles.some(role =>
    [ROLES.PROJECT_MANAGER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTS_ADMIN].includes(
      role
    )
  );
};
